
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/use-subscription';
import { usePaymentHistory } from '@/hooks/use-payment-history';
import { useSubscriptionFunctions } from '@/hooks/use-subscription-functions';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { 
  Loader2, 
  CreditCard, 
  FileText, 
  ArrowUp, 
  Download,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export default function BillingPage() {
  const [activeTab, setActiveTab] = useState('subscription');
  const { isAuthenticated, user } = useAuth();
  const { subscription, loading: subscriptionLoading, fetchUserSubscription } = useSubscription();
  const { payments, invoices, isLoading: historyLoading, fetchPaymentHistory } = usePaymentHistory();
  const { handleManageSubscription, checkSubscriptionStatus } = useSubscriptionFunctions();
  const { toast } = useToast();
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };
  
  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free': return 'Basic (Kostenlos)';
      case 'pro': return 'Premium (1€)';
      case 'studio_free': return 'Studio Free';
      case 'studio_pro': return 'Studio Pro (19€)';
      case 'studio_business': return 'Business (39€)';
      case 'studio_premium': return 'Premium (59€)';
      default: return plan;
    }
  };
  
  const handleRefreshSubscription = async () => {
    try {
      toast({
        title: "Aktualisiere Abonnement...",
        description: "Die aktuellen Daten werden abgerufen",
      });
      
      await checkSubscriptionStatus();
      await fetchUserSubscription();
      
      toast({
        title: "Abonnement aktualisiert",
        description: "Die Abonnementdaten wurden erfolgreich aktualisiert",
      });
    } catch (error) {
      toast({
        title: "Fehler bei der Aktualisierung",
        description: "Es gab ein Problem beim Aktualisieren des Abonnements",
        variant: "destructive",
      });
    }
  };
  
  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated && !subscriptionLoading) {
      navigate('/auth?redirect=/account/billing');
    }
  }, [isAuthenticated, subscriptionLoading]);

  if (!isAuthenticated) {
    return (
      <div className="container py-12">
        <Card>
          <CardHeader>
            <CardTitle>Anmeldung erforderlich</CardTitle>
            <CardDescription>Bitte melden Sie sich an, um Ihre Rechnungsinformationen zu sehen.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/auth?redirect=/account/billing">Zum Login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (subscriptionLoading) {
    return (
      <div className="container py-12 flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
      </div>
    );
  }

  return (
    <div className="container py-12">
      <h1 className="text-2xl font-bold mb-8">Abonnement & Rechnungen</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="subscription">Abonnement</TabsTrigger>
          <TabsTrigger value="payments">Zahlungen</TabsTrigger>
          <TabsTrigger value="invoices">Rechnungen</TabsTrigger>
        </TabsList>
        
        <TabsContent value="subscription" className="mt-6 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Aktuelles Abonnement</CardTitle>
                  <CardDescription>
                    Details zu Ihrem aktuellen Abonnement
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={handleRefreshSubscription}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg border">
                  <p className="text-sm text-muted-foreground mb-1">Plan</p>
                  <p className="font-semibold">{getPlanDisplayName(subscription?.plan || 'free')}</p>
                </div>
                <div className="p-4 rounded-lg border">
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${subscription?.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="font-semibold">{subscription?.isActive ? 'Aktiv' : 'Inaktiv'}</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg border">
                  <p className="text-sm text-muted-foreground mb-1">Nächste Zahlung</p>
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4" />
                    <p className="font-semibold">{subscription?.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : '-'}</p>
                  </div>
                </div>
              </div>
              
              {subscription?.plan.includes('studio_') && (
                <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-lg mb-6">
                  <h3 className="text-sm font-semibold mb-2">Kontingente & Limits</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Buchungen</p>
                      <p>{subscription?.quotaAppointments === null ? 'Unbegrenzt' : `${subscription?.quotaAppointments} pro Monat`}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                      <p>{subscription?.quotaStaff === null ? 'Unbegrenzt' : subscription?.quotaStaff}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter>
              <div className="flex flex-col md:flex-row gap-4 w-full">
                <Button 
                  className="w-full md:w-auto" 
                  onClick={() => navigate('/pricing')}
                >
                  <ArrowUp className="mr-2 h-4 w-4" />
                  Plan wechseln
                </Button>
                <Button 
                  className="w-full md:w-auto" 
                  variant="outline"
                  onClick={() => handleManageSubscription()}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Zahlungsmethode verwalten
                </Button>
              </div>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="payments" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Zahlungsverlauf</CardTitle>
              <CardDescription>
                Alle Zahlungen für Ihr Konto
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
                </div>
              ) : payments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Betrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Beschreibung</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(payment.created_at)}</TableCell>
                        <TableCell>{getPlanDisplayName(payment.plan)}</TableCell>
                        <TableCell>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'success' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' 
                              : payment.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                          }`}>
                            {payment.status === 'success' ? 'Erfolgreich' :
                             payment.status === 'pending' ? 'Ausstehend' : 'Fehlgeschlagen'}
                          </span>
                        </TableCell>
                        <TableCell>{payment.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">Keine Zahlungen gefunden</h3>
                  <p className="text-muted-foreground mt-2">
                    Es wurden noch keine Zahlungen für dieses Konto verarbeitet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="invoices" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Rechnungen</CardTitle>
              <CardDescription>
                Zum herunterladen verfügbare Rechnungen
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
                </div>
              ) : invoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Betrag</TableHead>
                      <TableHead>Währung</TableHead>
                      <TableHead className="text-right">Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{formatDate(invoice.created_at)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>{invoice.currency}</TableCell>
                        <TableCell className="text-right">
                          {invoice.pdf_url ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4 mr-2" />
                                PDF
                              </a>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">Nicht verfügbar</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">Keine Rechnungen gefunden</h3>
                  <p className="text-muted-foreground mt-2">
                    Es wurden noch keine Rechnungen für dieses Konto ausgestellt.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
